import {
	currentTargetRequired,
	getAttributeOrThrow,
	qsaRequired,
	qsOptional,
	qsRequired,
	removeTrapFocus,
	trapFocus,
} from '@/scripts/core/global'
import { UcoastEl } from '@/scripts/core/UcoastEl'
import EmblaCarousel, { type EmblaCarouselType, type EmblaOptionsType } from 'embla-carousel'
import { WheelGesturesPlugin } from 'embla-carousel-wheel-gestures'
import ClassNames from 'embla-carousel-class-names'
import { routes } from '@/scripts/setup'
import type { CartDrawer } from '@/scripts/cart/cart-drawer'
import {
	addItemsToCart,
	type CartAddWithSections,
	type CartUpdateWithSections,
} from '@/scripts/core/cart-functions'
import { enqueueDebouncedCartOperation } from '@/scripts/cart/cart-queue'

const CART_SECTIONS = ['cart-drawer', 'cart-icon-bubble']
const DEBOUNCE_KEY = 'cart-lifestyle'

type CartJsLine = {
	key: string
	variant_id: number
}

export class CartLifestyle extends UcoastEl {
	static htmlSelector = 'cart-lifestyle'
	summary: HTMLElement
	details: HTMLDetailsElement
	items: NodeListOf<HTMLButtonElement>
	boundOnKeyUpEscape: (event: KeyboardEvent) => void
	emblaApi?: EmblaCarouselType
	// Tile variants in the cart, per the last server render
	inCartIds: Set<number>
	// Tile clicks not yet applied to the cart (variant → wanted in cart).
	// Overlays inCartIds so re-renders don't revert tiles mid-debounce.
	pendingChanges = new Map<number, boolean>()

	constructor() {
		super()
		this.summary = qsRequired('summary', this)
		this.details = qsRequired('details', this)
		this.items = qsaRequired('[data-uc-lifestyle]', this)
		this.boundOnKeyUpEscape = this.onKeyUpEscape.bind(this)
		this.summary.addEventListener('click', this.onSummaryClick.bind(this))
		this.items.forEach((item) => {
			item.addEventListener('click', this.toggleItem.bind(this))
		})
		this.inCartIds = new Set(this.getActiveIds(this))
		this.initialize()
	}

	initialize() {
		if (this.isOpen()) {
			const height = this.details.scrollHeight
			if (height > 0) {
				this.details.style.maxHeight = `${height}px`
			}
			this.getCartDrawer().classList.add('lifestyle-active')
			window.setTimeout(() => {
				window.Ucoast.mediaManager.loadAllInContainer(this)
			}, 4)
		}
		this.updateFixedAreaHeight()
		this.initEmbla()
	}

	initEmbla() {
		const viewportNode = qsOptional('.embla__viewport', this)
		if (!viewportNode) return
		const OPTIONS: EmblaOptionsType = {
			align: 'start',
			containScroll: 'keepSnaps',
			dragFree: true,
			duration: 100,
			dragThreshold: 100,
		}
		try {
			this.emblaApi = EmblaCarousel(viewportNode, OPTIONS, [
				WheelGesturesPlugin(),
				ClassNames(),
			])
		} catch (e) {
			console.error(e)
		}
	}

	getVariantId(item: HTMLElement) {
		return parseInt(getAttributeOrThrow('data-uc-lifestyle', item))
	}

	getActiveIds(container: Element) {
		return Array.from(
			container.querySelectorAll<HTMLElement>('[data-uc-lifestyle].active')
		).map((item) => this.getVariantId(item))
	}

	toggleItem(event: Event) {
		event.preventDefault()
		const target = currentTargetRequired<Event, HTMLButtonElement>(event)
		const variantId = this.getVariantId(target)
		if (isNaN(variantId)) return
		this.pendingChanges.set(variantId, !this.isSelected(variantId))
		this.toggleActiveItems()
		enqueueDebouncedCartOperation(DEBOUNCE_KEY, () => this.syncCart())
	}

	isSelected(variantId: number) {
		return this.pendingChanges.get(variantId) ?? this.inCartIds.has(variantId)
	}

	toggleActiveItems() {
		this.items.forEach((item) => {
			item.classList.toggle(
				'active',
				this.isSelected(this.getVariantId(item))
			)
		})
	}

	/**
	 * Apply pending tile clicks to the cart. Only clicked variants are touched,
	 * so lines changed elsewhere meanwhile (e.g. removed in the drawer) stay put.
	 * Deselecting removes every line of that variant, however it was added.
	 */
	async syncCart() {
		const changes = new Map(this.pendingChanges)
		if (!changes.size) return
		const drawer = this.getCartDrawer() as CartDrawer
		// Drop applied changes unless re-clicked meanwhile (that click queued another sync)
		const clearApplied = () => {
			changes.forEach((wanted, id) => {
				if (this.pendingChanges.get(id) === wanted) {
					this.pendingChanges.delete(id)
				}
			})
		}
		try {
			const cart = (await (await fetch('/cart.js')).json()) as {
				items: CartJsLine[]
			}
			const removals: Record<string, number> = {}
			cart.items.forEach((line) => {
				if (changes.get(line.variant_id) === false) removals[line.key] = 0
			})
			const inCart = new Set(cart.items.map((line) => line.variant_id))
			const additions = Array.from(changes)
				.filter(([id, wanted]) => wanted && !inCart.has(id))
				.map(([id]) => ({ id, quantity: 1, properties: {} }))

			let state: CartUpdateWithSections | CartAddWithSections | undefined
			if (Object.keys(removals).length) {
				const response = await fetch(`${routes.cart_update_url}`, {
					method: 'POST',
					headers: { 'Content-Type': 'application/json' },
					body: JSON.stringify({
						updates: removals,
						sections: CART_SECTIONS,
						sections_url: window.location.pathname,
					}),
				})
				state = (await response.json()) as CartUpdateWithSections
			}
			if (additions.length) {
				const result = await addItemsToCart(additions, CART_SECTIONS)
				// e.g. sold out — tile falls back to server state below
				if ('status' in result) console.error(result)
				else state = result as CartAddWithSections
			}

			clearApplied()
			if (state) drawer.renderContentsWithoutOpening(state)
			else this.toggleActiveItems()
		} catch (e) {
			console.error(e)
			// Resync tiles from server state
			clearApplied()
			await drawer.reloadWithoutOpening()
		}
	}

	/** Adopt server tile states, keeping clicks not yet synced. */
	syncItemStates(fresh: Element) {
		this.inCartIds = new Set(this.getActiveIds(fresh))
		this.toggleActiveItems()
	}

	onSummaryClick(event: Event) {
		event.preventDefault()
		return this.isOpen() ? this.close() : this.open()
	}

	getCartDrawer() {
		return qsRequired('cart-drawer')
	}

	getSummaryHeight() {
		return this.summary.scrollHeight
	}

	isOpen() {
		return this.details.open
	}

	open() {
		this.details.open = true
		this.details.style.maxHeight = `${this.details.scrollHeight}px`
		this.getCartDrawer().classList.add('lifestyle-active')
		this.updateFixedAreaHeight()
		trapFocus(this.details)
		this.details.addEventListener('keyup', this.boundOnKeyUpEscape)
		window.setTimeout(() => {
			window.Ucoast.mediaManager.loadAllInContainer(this)
		}, 4)
	}

	close() {
		this.details.style.maxHeight = `${this.getSummaryHeight()}px`
		this.getCartDrawer().classList.remove('lifestyle-active')
		window.setTimeout(() => {
			this.details.open = false
			this.updateFixedAreaHeight()
		}, 301)
		removeTrapFocus(this.summary)
	}

	updateFixedAreaHeight() {
		const drawer = this.getCartDrawer()
		if (this.isOpen()) {
			const expandedOffset = this.details.scrollHeight - this.getSummaryHeight()
			drawer.style.setProperty('--lifestyle-expanded-offset', `${expandedOffset}px`)
		} else {
			drawer.style.setProperty('--lifestyle-expanded-offset', '0px')
		}
	}

	onKeyUpEscape(event: KeyboardEvent) {
		if (event.code.toUpperCase() !== 'ESCAPE') return
		if (!this.isOpen()) return
		event.stopPropagation()
		this.details.removeEventListener('keyup', this.boundOnKeyUpEscape)
		this.close()
	}

	override disconnectedCallback() {
		super.disconnectedCallback()
		this.emblaApi?.destroy()
		const drawer = qsOptional('cart-drawer')
		if (drawer) {
			drawer.style.setProperty('--lifestyle-expanded-offset', '0px')
			drawer.classList.remove('lifestyle-active')
		}
	}
}
